export default function Hero() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-rose-50 to-sky-50">
      {/* Decorative background blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-rose-100/60 rounded-full -translate-y-1/3 translate-x-1/3 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-sky-100/60 rounded-full translate-y-1/3 -translate-x-1/3 blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-50/40 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
              <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse inline-block"></span>
              <span className="text-sm font-semibold text-teal-700">Pregnancy &amp; Baby Care Platform</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight">
              One Platform for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-sky-500">
                Pregnancy &amp; Baby Care
              </span>
              <br />
              From First Week to 5 Years
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Track your pregnancy, find the right hospitals, and never miss a vaccine. All in one place.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <button
                onClick={() => scrollToSection('how-it-works')}
                className="px-8 py-4 bg-gradient-to-r from-teal-600 to-sky-500 text-white rounded-xl font-bold text-base sm:text-lg hover:from-teal-700 hover:to-sky-600 transition-all hover:-translate-y-1 whitespace-nowrap"
              >
                Get Started Free
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="px-8 py-4 bg-white border-2 border-teal-200 text-teal-700 rounded-xl font-bold text-base sm:text-lg hover:bg-teal-50 hover:border-teal-400 transition-all hover:-translate-y-1 whitespace-nowrap"
              >
                Explore Care Plan
              </button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <i className="ri-government-line text-teal-500 text-lg"></i>
                <span>Govt. of India Guidelines</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <i className="ri-shield-check-line text-teal-500 text-lg"></i>
                <span>100% Free to Use</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <i className="ri-hospital-line text-teal-500 text-lg"></i>
                <span>21+ Hospitals</span>
              </div>
            </div>
          </div>

          {/* Right: Visual card stack */}
          <div className="flex flex-col gap-4 lg:pl-8">
            <div className="relative rounded-3xl overflow-hidden h-56 sm:h-64">
              <img
                src="https://public.readdy.ai/ai/img_res/23efde117e07808a72f3573cd4629b82.jpg"
                alt="Expecting mother"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 flex items-center gap-2">
                <i className="ri-heart-pulse-line text-rose-500 text-lg"></i>
                <span className="text-sm font-semibold text-gray-800">All looking great!</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white border border-rose-100 rounded-2xl p-4 text-center hover:-translate-y-1 transition-transform">
                <div className="w-10 h-10 flex items-center justify-center bg-rose-50 rounded-xl mx-auto mb-2">
                  <i className="ri-calendar-check-line text-rose-500 text-xl"></i>
                </div>
                <div className="text-xl font-bold text-gray-900">40</div>
                <div className="text-xs text-gray-500">Weeks Tracked</div>
              </div>
              <div className="bg-white border border-teal-100 rounded-2xl p-4 text-center hover:-translate-y-1 transition-transform">
                <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl mx-auto mb-2">
                  <i className="ri-hospital-line text-teal-500 text-xl"></i>
                </div>
                <div className="text-xl font-bold text-gray-900">21+</div>
                <div className="text-xs text-gray-500">Hospitals</div>
              </div>
              <div className="bg-white border border-sky-100 rounded-2xl p-4 text-center hover:-translate-y-1 transition-transform">
                <div className="w-10 h-10 flex items-center justify-center bg-sky-50 rounded-xl mx-auto mb-2">
                  <i className="ri-syringe-line text-sky-500 text-xl"></i>
                </div>
                <div className="text-xl font-bold text-gray-900">0–5</div>
                <div className="text-xs text-gray-500">Baby Care Yrs</div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-600 to-sky-500 rounded-2xl p-5 flex items-center gap-4 text-white">
              <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
                <i className="ri-notification-3-line text-2xl"></i>
              </div>
              <div>
                <div className="font-bold text-sm">Next Vaccine Due</div>
                <div className="text-white/80 text-xs mt-0.5">BCG at Birth — DPT at 6 weeks</div>
              </div>
              <div className="ml-auto">
                <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">Smart Alert</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
